/**
 * Estado de sesión del Gourmetero.
 *
 * GU-AUTH-01: el Modo Explorador es el estado por defecto. No hay sesión,
 * no hay permisos, no hay modales — y aun así la app es completamente
 * usable para descubrir.
 *
 * GU-AUTH-06: la sesión persiste entre aperturas de la app.
 */

import { create } from 'zustand';
import type { GatedAction } from '@gu/contracts';

import type { Me } from '../../shared/lib/api.js';

/**
 * Acción que el usuario intentó antes de toparse con la puerta de auth.
 * Se preserva para devolverlo exactamente donde estaba: GU-AUTH-01 exige
 * regresar "a la acción que intentaba, con el contexto preservado".
 */
export interface PendingIntent {
  action: GatedAction;
  /** place_id, collection_id… según la acción. */
  targetId?: string;
  /** Ruta a la que volver tras autenticar. */
  returnTo: string;
}

/**
 * El perfil PROPIO, no el público.
 *
 * `GourmeteroProfile` de los contratos describe lo que se ve junto a una
 * recomendación, donde el nombre nunca es nulo — no se renderiza una
 * opinión sin cara. Aquí el perfil puede estar a medias: una cuenta
 * recién nacida por OTP no tiene ni nombre ni avatar hasta la pantalla 4,
 * y ese hueco es justo lo que empuja a pedirlos.
 */
interface SessionState {
  profile: Me | null;
  status: 'loading' | 'anonymous' | 'authenticated';
  pendingIntent: PendingIntent | null;

  setProfile: (profile: Me | null) => void;
  setStatus: (status: SessionState['status']) => void;
  /** Guarda la intención al chocar con la puerta de auth. */
  gate: (intent: PendingIntent) => void;
  /** Consume la intención pendiente (una sola vez). */
  consumeIntent: () => PendingIntent | null;
  signOut: () => void;
}

export const useSession = create<SessionState>((set, get) => ({
  profile: null,
  status: 'loading',
  pendingIntent: null,

  setProfile: (profile) =>
    set({ profile, status: profile ? 'authenticated' : 'anonymous' }),

  setStatus: (status) => set({ status }),

  gate: (intent) => set({ pendingIntent: intent }),

  consumeIntent: () => {
    const intent = get().pendingIntent;
    if (intent) set({ pendingIntent: null });
    return intent;
  },

  signOut: () =>
    set({ profile: null, status: 'anonymous', pendingIntent: null }),
}));

/** ¿Está en Modo Explorador? Es el estado por defecto, no un error. */
export function useIsExplorador(): boolean {
  return useSession((s) => s.status !== 'authenticated');
}

/**
 * ¿Puede publicar contenido público?
 *
 * Distinto de "tiene sesión": GU-AUTH-04 exige nombre y avatar. Un usuario
 * autenticado con perfil incompleto NO puede publicar, y la UI debe saberlo
 * antes de ofrecer el botón — no descubrirlo con un 409 del servidor.
 */
export function useCanPublish(): boolean {
  // Lo decide el SERVIDOR y viaja en el perfil: si la PWA lo recalculara
  // por su cuenta, un día ofrecería publicar y el servidor lo rechazaría.
  return useSession((s) => s.profile?.canPublish ?? false);
}
